import ClickableLink from 'components/Common/Clickable/ClickableLink'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import Heading from 'components/Luxkit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextButton from 'components/Luxkit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import ExperienceGiveAsGiftInfoText from 'components/Experiences/ExperienceGiveAsGiftModal/ExperienceGiveAsGiftInfoText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import List from 'components/Common/List'
import Typography from 'components/Luxkit/Typography/Typography'
import ExperienceBookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/ExperienceBookmarkButton'
import Breakpoint from 'components/Common/Breakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import ExperienceImageCarousel from './ExperienceImageCarousel'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { useInView } from 'react-intersection-observer'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'

const Experience = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  overflow: hidden;
  position: relative;

  ${mediaQueryUp.tablet} {
    height: ${rem(480)};
    grid-template-columns: 1fr ${rem(390)};
  }
`

const Description = styled(ClickableLink)`
  display: flex;
  flex-direction: column;
  padding: ${rem(20)};
  overflow: hidden;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(32)};
    text-align: center;
    gap: ${rem(16)};
    max-height: ${rem(480)};
  }
`

const DescriptionTopSection = styled(VerticalSpacer)`
  flex-grow: 1;
  display: flex;
  overflow: hidden;
  position: relative;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    justify-content: center;
  }
`

const PriceSection = styled(VerticalSpacer)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  ${mediaQueryUp.tablet} {
    align-items: stretch;
    flex-direction: column;
  }
`

const CenteredCancellationText = styled(ExperienceCancellationText)`
  ${mediaQueryUp.tablet} {
    margin-left: auto;
    margin-right: auto;
  }
`

const CenteredGiftText = styled(ExperienceGiveAsGiftInfoText)`
  ${mediaQueryUp.tablet} {
    margin-left: auto;
    margin-right: auto;
  }
`

const TypographyBox = styled(Typography)`
  &.plus-more {
    left: 0;
    width: 100%;
    display: block;
    bottom: ${rem(-4)};
    position: absolute;
    padding: ${rem(24)} 0 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 1) 50%
    );

    ${mediaHoverable} {
      > span:hover {
        opacity: 0.8;
      }
    }
  }
`

interface Props {
  experience: App.ExperienceOffer;
  position?: number;
}

function ExperienceHeroTile(props: Props) {
  const { experience, position } = props
  const containerElement = useRef<HTMLDivElement>(null)
  const textElement = useRef<HTMLDivElement>(null)
  const [showPlusMore, setShowPlusMore] = useState<boolean>(false)
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [inViewRef, hasBeenInView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  useEffect(() => {
    if (hasBeenInView && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: experience,
        position,
        key: EventDataKey.ProductImpression,
      })
    }
  }, [hasBeenInView, experience, dispatchOfferListEvent, position])

  const onClick = useCallback(() => {
    if (position != undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: experience,
        position,
        key: EventDataKey.ProductClick,
      })
    }
  }, [experience, dispatchOfferListEvent, position])

  useEffect(() => {
    const isBiggerThanHeight = textElement.current?.clientHeight > containerElement.current?.clientHeight
    setShowPlusMore(isBiggerThanHeight)
  }, [])

  return (
    <Experience ref={inViewRef}>
      <ExperienceImageCarousel experience={experience}/>
      <TripGuard>
        <CSSBreakpoint max="mobile">
          <FloatingBookmarkContainer>
            <ExperienceBookmarkButton offer={experience} />
          </FloatingBookmarkContainer>
        </CSSBreakpoint>
      </TripGuard>
      <Description to={getExperienceOfferPageURL(experience)} onClick={onClick} >
        <TripGuard>
          <CSSBreakpoint min="tablet">
            <OffsetBookmarkContainer>
              <ExperienceBookmarkButton offer={experience} />
            </OffsetBookmarkContainer>
          </CSSBreakpoint>
        </TripGuard>
        <DescriptionTopSection gap={8}>
          {experience.primaryCategory && <BodyText variant="small" colour="neutral-two">
            {experience.primaryCategory.name}
          </BodyText>}
          <div>
            <Subtitle variant="subtitle3">{experience.location.name}</Subtitle>
            <Heading variant="heading6">{experience.name}</Heading>
          </div>
          <CenteredCancellationText cancellationInfo={experience.copy.cancellationInfo} />
          {!!experience.giftType && <CenteredGiftText experience={experience} />}
          <div style={{ overflow: 'hidden' }} ref={containerElement}>
            <TypographyBox ref={textElement} align="start">
              {experience.productType !== 'channel_manager' && <MarkdownRender type="compact" fontSize="small" content={experience.copy.included || experience.copy.description} />}
              {experience.productType === 'channel_manager' && <>
                {experience.copy.highlights.length === 0 && <MarkdownRender type="compact" fontSize="small" content={experience.copy.description} />}
                {experience.copy.highlights.length > 0 && <List>
                  {experience.copy.highlights.map(highlight => <li key={highlight}>
                    <BodyText variant="small" as="span">{highlight}</BodyText>
                  </li>)}
                </List>}
              </>}
            </TypographyBox>
          </div>
          {showPlusMore && (
            <TypographyBox align="start" className="plus-more">
              <BodyText weight="bold" colour="primary" variant="small" as="span">Plus more...</BodyText>
            </TypographyBox>
          )}
        </DescriptionTopSection>
        <PriceSection gap={16}>
          <div>
            <PriceRowPriceCaption>From</PriceRowPriceCaption>
            <Heading variant="heading3"><FormatCurrency value={experience.price} /></Heading>
            {experience.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && <PriceRowValueDiscountWithCaption
              data-testid="value-box"
              size="M"
              originalValue={Math.ceil(experience.grossPrice)}
              discountPercentage={experience.discount}
            />}
          </div>
          <TextButton kind="primary" nonInteractable>
            <Breakpoint min="tablet">View offer</Breakpoint>
            <Breakpoint max="mobile">View</Breakpoint>
          </TextButton>
        </PriceSection>
      </Description>
    </Experience>
  )
}

export default ExperienceHeroTile
